import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { ticketApi } from '../api/tickets';
import { customerApi } from '../api/machines';
import { useAuth } from '../context/AuthContext';
import { useIsMobile } from '../hooks/useIsMobile';
import type { Ticket, TicketStatus, TicketPriority, Customer } from '../types';

const STATUS_LABEL: Record<TicketStatus, string> = {
  NEW: 'Nowy',
  IN_PROGRESS: 'W trakcie',
  AWAITING_CUSTOMER: 'Czeka na klienta',
  CLOSED: 'Zamknięte',
};

const PRIORITY_LABEL: Record<TicketPriority, string> = {
  LOW: 'Niski',
  NORMAL: 'Normalny',
  HIGH: 'Wysoki',
  CRITICAL: 'Krytyczny',
};

const PRIORITY_ORDER: Record<TicketPriority, number> = {
  CRITICAL: 0, HIGH: 1, NORMAL: 2, LOW: 3,
};

const priorityColor = (p: TicketPriority): string => {
  switch (p) {
    case 'CRITICAL': return 'var(--color-danger)';
    case 'HIGH': return 'var(--color-warning)';
    case 'NORMAL': return 'var(--color-text-secondary)';
    case 'LOW': return 'var(--color-text-muted)';
  }
};

const MyTickets: React.FC = () => {
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState<'assigned' | 'unassigned'>('assigned');
  const [includeClosed, setIncludeClosed] = useState(false);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selectedNip, setSelectedNip] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await ticketApi.listMine(includeClosed);
      setTickets(data);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Nie udało się pobrać zgłoszeń');
    } finally {
      setLoading(false);
    }
  }, [includeClosed]);

  useEffect(() => { load(); }, [load]);

  // Customers serviced by this integrator — powers the customer-name autocomplete.
  useEffect(() => {
    customerApi.getAll(true).then(({ data }) => setCustomers(data)).catch(() => { /* ignore */ });
  }, []);

  const isStaff = user?.roles?.includes('ADMIN') || user?.roles?.includes('EMPLOYEE_ADMIN') || user?.roles?.includes('EMPLOYEE');
  const me = user?.userId;

  const assigned = useMemo(
    () => tickets.filter((t) => me && t.assignedToUserId === me)
      .sort((a, b) => PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority]
        || b.createdAt.localeCompare(a.createdAt)),
    [tickets, me]
  );
  const unassigned = useMemo(
    () => tickets.filter((t) => !t.assignedToUserId)
      .sort((a, b) => PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority]
        || b.createdAt.localeCompare(a.createdAt)),
    [tickets]
  );

  const nipToName = useMemo(() => {
    const m: Record<string, string> = {};
    customers.forEach((c) => { if (c.nip) m[c.nip] = c.companyName || ''; });
    return m;
  }, [customers]);

  // Only customers that actually have tickets in the current list.
  const customerOptions = useMemo(() => {
    const nips = Array.from(new Set(tickets.map((t) => t.customerNip).filter(Boolean) as string[]));
    return nips
      .map((nip) => ({ nip, label: nipToName[nip] ? `${nipToName[nip]} (NIP ${nip})` : `NIP ${nip}` }))
      .sort((a, b) => a.label.localeCompare(b.label, 'pl'));
  }, [tickets, nipToName]);

  if (!isStaff) return <Navigate to="/machines" replace />;

  const byCustomer = (list: Ticket[]) =>
    selectedNip ? list.filter((t) => t.customerNip === selectedNip) : list;

  const shown = byCustomer(tab === 'assigned' ? assigned : unassigned);

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Zgłoszenia</h1>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem' }}>
          <input
            type="checkbox"
            checked={includeClosed}
            onChange={(e) => setIncludeClosed(e.target.checked)}
          />
          Pokaż zamknięte
        </label>
      </div>

      {customerOptions.length > 0 && (
        <div className="filter-group" style={{ marginBottom: '1rem', maxWidth: 420 }}>
          <label className="form-label">Klient</label>
          <select className="form-select" value={selectedNip} onChange={(e) => setSelectedNip(e.target.value)}>
            <option value="">Wszyscy klienci</option>
            {customerOptions.map((c) => (
              <option key={c.nip} value={c.nip}>{c.label}</option>
            ))}
          </select>
        </div>
      )}

      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', borderBottom: '1px solid var(--color-border)' }}>
        <button
          onClick={() => setTab('assigned')}
          className={`tab-btn ${tab === 'assigned' ? 'active' : ''}`}
          style={tabStyle(tab === 'assigned')}
        >
          Przypisane do mnie <span style={countBadge}>{assigned.length}</span>
        </button>
        <button
          onClick={() => setTab('unassigned')}
          className={`tab-btn ${tab === 'unassigned' ? 'active' : ''}`}
          style={tabStyle(tab === 'unassigned')}
        >
          Nieprzypisane <span style={countBadge}>{unassigned.length}</span>
        </button>
      </div>

      {error && <div className="auth-error">{error}</div>}
      {loading ? (
        <div className="empty-state">Ładowanie…</div>
      ) : shown.length === 0 ? (
        <div className="empty-state">
          {selectedNip
            ? 'Brak zgłoszeń dla wybranego klienta.'
            : tab === 'assigned'
              ? 'Nie masz aktualnie przypisanych zgłoszeń.'
              : 'Brak nieprzypisanych zgłoszeń.'}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {shown.map((t) => {
            const priorityBadge = (
              <span style={{
                background: priorityColor(t.priority),
                color: '#fff',
                fontSize: '0.6875rem',
                fontWeight: 600,
                padding: '0.125rem 0.5rem',
                borderRadius: 4,
                textTransform: 'uppercase',
                letterSpacing: '0.03em',
                whiteSpace: 'nowrap',
              }}>
                {PRIORITY_LABEL[t.priority]}
              </span>
            );
            const statusBadge = (
              <span style={{
                fontSize: '0.6875rem',
                fontWeight: 600,
                padding: '0.125rem 0.5rem',
                borderRadius: 4,
                whiteSpace: 'nowrap',
                background: (t.status === 'NEW' || t.status === 'IN_PROGRESS') ? '#e2eefb' : t.status === 'CLOSED' ? '#dcefe2' : '#e7eaee',
                color: (t.status === 'NEW' || t.status === 'IN_PROGRESS') ? '#17538f' : t.status === 'CLOSED' ? '#1f7a42' : '#424b56',
              }}>
                {STATUS_LABEL[t.status]}
              </span>
            );
            // Lewa kolumna („co jest teraz”): nagłówek + metadane. Na desktopie 20% szerokości,
            // na mobile zajmuje cały kafelek (bez prawej kolumny z treścią).
            const leftColumn = (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem', minWidth: 0 }}>
                {isMobile ? (
                  /* Mobile: tytuł w lewym górnym rogu, status + priorytet w prawym (jeden pod drugim). */
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.5rem' }}>
                    <strong style={{ fontSize: '0.9375rem', flex: 1, minWidth: 0 }}>{t.title}</strong>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.25rem', flexShrink: 0 }}>
                      {statusBadge}
                      {priorityBadge}
                    </div>
                  </div>
                ) : (
                  /* Desktop: badge'y w pierwszej linii, tytuł łamany do nowej linii pod nimi. */
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                      {priorityBadge}
                      {statusBadge}
                    </div>
                    <strong style={{ fontSize: '0.9375rem' }}>{t.title}</strong>
                  </div>
                )}
                {/* Każda informacja w osobnej linii (mobile i desktop): maszyna, od, NIP, data. */}
                <div className="item-meta" style={{ fontSize: '0.8125rem', display: 'flex', flexDirection: 'column', gap: '0.125rem' }}>
                  {t.machineName && <span>Maszyna: <strong>{t.machineName}</strong></span>}
                  {t.createdByFirstName && <span>Od: {t.createdByFirstName} {t.createdByLastName}</span>}
                  {t.customerNip && <span>NIP: {t.customerNip}</span>}
                  <span>{new Date(t.createdAt).toLocaleString('pl-PL')}</span>
                </div>
              </div>
            );
            return (
            <Link
              key={t.id}
              to={`/t/${t.id}`}
              className="card"
              style={{
                padding: '0.875rem 1rem',
                textDecoration: 'none',
                color: 'inherit',
                display: isMobile ? 'flex' : 'grid',
                ...(isMobile
                  ? { flexDirection: 'column', gap: '0.375rem' }
                  : { gridTemplateColumns: '20% 80%', gap: '1rem', alignItems: 'stretch' }),
              }}
            >
              {leftColumn}
              {/* Desktop-only: prawa kolumna (80%) z treścią zgłoszenia od klienta. */}
              {!isMobile && (
                <div style={{
                  minWidth: 0,
                  borderLeft: '1px solid var(--color-border)',
                  paddingLeft: '1rem',
                  fontSize: '0.875rem',
                  lineHeight: 1.45,
                  color: 'var(--color-text)',
                  whiteSpace: 'pre-wrap',
                  overflowWrap: 'anywhere',
                }}>
                  {t.firstMessage && t.firstMessage.trim()
                    ? t.firstMessage
                    : <span style={{ color: 'var(--color-text-muted)', fontStyle: 'italic' }}>Brak treści zgłoszenia</span>}
                </div>
              )}
            </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

const tabStyle = (active: boolean): React.CSSProperties => ({
  padding: '0.625rem 1rem',
  background: 'transparent',
  border: 'none',
  borderBottom: active ? '2px solid var(--color-accent)' : '2px solid transparent',
  cursor: 'pointer',
  fontSize: '0.9375rem',
  fontWeight: active ? 600 : 500,
  color: active ? 'var(--color-text)' : 'var(--color-text-muted)',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.5rem',
});

const countBadge: React.CSSProperties = {
  background: 'var(--color-surface-alt)',
  color: 'var(--color-text-muted)',
  borderRadius: 10,
  padding: '0.0625rem 0.5rem',
  fontSize: '0.75rem',
  fontWeight: 600,
};

export default MyTickets;
